import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

type BrandProduct = {
  name: string;
  category: string;
  warranty: string;
  highlights: string[];
};

type Brand = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  status: "active" | "coming";
  icon: string;
  accentColor: string;
  badgeColor: string;
  since: string;
  website: string;
  shopFilter: string;
  warrantyUnit: string;
  warrantyBattery: string;
  warrantyNotes: string;
  products: BrandProduct[];
  docStatus: { label: string; verified: boolean }[];
};

const brands: Brand[] = [
  {
    id: "renogy",
    name: "Renogy",
    tagline: "Solar Panels, Charge Controllers & Off-Grid Kits",
    description:
      "Renogy is one of the most trusted names in solar energy equipment, known for high-efficiency panels, MPPT charge controllers, and complete off-grid solar kits. Iron Haven Supply is an authorized Renogy reseller — every purchase includes full manufacturer warranty and genuine Renogy support.",
    status: "active",
    icon: "ri-sun-line",
    accentColor: "text-orange-600",
    badgeColor: "bg-orange-50 border-orange-200 text-orange-700",
    since: "2023",
    website: "https://www.renogy.com",
    shopFilter: "/shop?brand=Renogy",
    warrantyUnit: "5 years (panels) / 2 years (electronics)",
    warrantyBattery: "2 years",
    warrantyNotes:
      "Renogy solar panels carry a 25-year power output guarantee (minimum 80% output through year 25). Charge controllers, inverters, and accessories carry a separate 2-year product warranty. All claims handled through Renogy US support with Iron Haven as your advocate.",
    products: [
      {
        name: "Renogy 200W Rigid Solar Panel",
        category: "Solar Panels",
        warranty: "5-year product / 25-year output",
        highlights: ["Monocrystalline PERC cells", "High conversion efficiency", "Corrosion-resistant aluminum frame", "Suitable for RV, cabin, off-grid"],
      },
      {
        name: "Renogy Wanderer 30A PWM Charge Controller",
        category: "Charge Controllers",
        warranty: "2 years",
        highlights: ["PWM technology", "12V/24V auto-detect", "LCD display", "Overcharge & short-circuit protection"],
      },
      {
        name: "Renogy Rover 40A MPPT Charge Controller",
        category: "Charge Controllers",
        warranty: "2 years",
        highlights: ["MPPT efficiency up to 99%", "Supports lithium & AGM batteries", "Bluetooth compatible", "Multiple load control modes"],
      },
      {
        name: "Renogy 200Ah Deep Cycle AGM Battery",
        category: "Batteries",
        warranty: "2 years",
        highlights: ["Maintenance-free sealed design", "Deep cycle rated", "Vibration resistant", "Wide temperature range"],
      },
      {
        name: "Renogy 2000W Pure Sine Wave Inverter",
        category: "Inverters",
        warranty: "2 years",
        highlights: ["Pure sine wave output", "USB charging ports", "Overload & over-temp protection", "Suitable for sensitive electronics"],
      },
      {
        name: "Renogy Complete 400W Solar Kit",
        category: "Solar Kits",
        warranty: "5-year product / 25-year output",
        highlights: ["4x 100W panels included", "MPPT controller", "All mounting hardware", "Plug-and-play wiring"],
      },
    ],
    docStatus: [
      { label: "Authorized Reseller Agreement", verified: true },
      { label: "Reseller Certificate on File", verified: true },
      { label: "Warranty Pass-Through Confirmed", verified: true },
      { label: "Sales Tax Compliance", verified: true },
    ],
  },
  {
    id: "natures-generator",
    name: "Nature's Generator",
    tagline: "Solar Generators & Whole Home Backup Systems",
    description:
      "Nature's Generator specializes in solar-powered backup systems designed for whole-home and critical-load use. Their expandable ecosystem — from portable units to whole-home transfer switch kits — makes them a standout choice for serious preparedness. Iron Haven Supply is a verified authorized dealer.",
    status: "active",
    icon: "ri-flashlight-line",
    accentColor: "text-emerald-600",
    badgeColor: "bg-emerald-50 border-emerald-200 text-emerald-700",
    since: "2023",
    website: "https://www.naturesgenerator.com",
    shopFilter: "/shop?brand=Nature%27s+Generator",
    warrantyUnit: "2 years",
    warrantyBattery: "2 years (80% capacity retention)",
    warrantyNotes:
      "Nature's Generator products carry a 2-year limited warranty covering defects in materials and workmanship. Battery modules guarantee 80% capacity retention through the warranty period. Expansion batteries and power pods carry independent 2-year coverage. Iron Haven Supply files and manages all claims on your behalf.",
    products: [
      {
        name: "Nature's Generator Gold System",
        category: "Solar Generators",
        warranty: "2 years",
        highlights: ["1800W continuous output", "720Wh battery capacity", "Solar & AC charging", "Expandable with Power Pods"],
      },
      {
        name: "Nature's Generator Platinum System",
        category: "Solar Generators",
        warranty: "2 years",
        highlights: ["1800W output", "Includes 100W solar panel", "Expandable ecosystem", "Quiet solar operation"],
      },
      {
        name: "Nature's Generator Elite",
        category: "Whole Home Backup",
        warranty: "2 years",
        highlights: ["7200Wh capacity", "Transfer switch compatible", "Whole-home critical load support", "Stackable Power Pods"],
      },
      {
        name: "Nature's Generator Power Pod",
        category: "Expansion Batteries",
        warranty: "2 years",
        highlights: ["720Wh expansion module", "Daisy-chain up to 4 pods", "Seamless integration", "Lithium iron phosphate cells"],
      },
      {
        name: "Nature's Generator Whole Home Transfer Switch Kit",
        category: "Whole Home Systems",
        warranty: "2 years",
        highlights: ["Automatic transfer switch", "Supports up to 10 circuits", "Professional install recommended", "Works with Elite & Platinum"],
      },
    ],
    docStatus: [
      { label: "Authorized Reseller Agreement", verified: true },
      { label: "Reseller Certificate on File", verified: true },
      { label: "Warranty Pass-Through Confirmed", verified: true },
      { label: "Sales Tax Compliance", verified: true },
    ],
  },
  {
    id: "rich-solar",
    name: "Rich Solar",
    tagline: "High-Efficiency Solar Panels & Off-Grid Components",
    description:
      "Rich Solar delivers premium monocrystalline solar panels and off-grid components at accessible price points without sacrificing quality. Their panels are widely used in RV, cabin, marine, and residential off-grid applications. Iron Haven Supply is an authorized Rich Solar dealer.",
    status: "active",
    icon: "ri-sun-foggy-line",
    accentColor: "text-amber-600",
    badgeColor: "bg-amber-50 border-amber-200 text-amber-700",
    since: "2024",
    website: "https://richsolar.com",
    shopFilter: "/shop?brand=Rich+Solar",
    warrantyUnit: "5 years (panels) / 2 years (electronics)",
    warrantyBattery: "2 years",
    warrantyNotes:
      "Rich Solar panels carry a 5-year product warranty and a 25-year linear power output warranty (minimum 80% output at year 25). Charge controllers and accessories carry a 2-year product warranty. All warranty claims are supported through Iron Haven Supply.",
    products: [
      {
        name: "Rich Solar 100W Monocrystalline Panel",
        category: "Solar Panels",
        warranty: "5-year product / 25-year output",
        highlights: ["High-efficiency mono cells", "Lightweight aluminum frame", "Pre-drilled mounting holes", "Ideal for RV & off-grid"],
      },
      {
        name: "Rich Solar 200W Monocrystalline Panel",
        category: "Solar Panels",
        warranty: "5-year product / 25-year output",
        highlights: ["200W peak output", "Tempered glass surface", "IP65 junction box", "Low light performance"],
      },
      {
        name: "Rich Solar 40A MPPT Charge Controller",
        category: "Charge Controllers",
        warranty: "2 years",
        highlights: ["MPPT algorithm", "12V/24V/36V/48V auto-detect", "LCD display", "Multiple battery type support"],
      },
      {
        name: "Rich Solar 400W Solar Kit",
        category: "Solar Kits",
        warranty: "5-year product / 25-year output",
        highlights: ["4x 100W panels", "MPPT controller included", "Complete wiring kit", "Expandable design"],
      },
    ],
    docStatus: [
      { label: "Authorized Reseller Agreement", verified: true },
      { label: "Reseller Certificate on File", verified: true },
      { label: "Warranty Pass-Through Confirmed", verified: true },
      { label: "Sales Tax Compliance", verified: true },
    ],
  },
  {
    id: "ecoflow",
    name: "EcoFlow",
    tagline: "Portable Power Stations & Smart Home Energy — Coming Soon",
    description:
      "EcoFlow is a global leader in portable power stations and smart home energy solutions, known for their DELTA and RIVER series. Iron Haven Supply is in the process of completing EcoFlow's authorized reseller onboarding. Once live, every EcoFlow purchase will include full manufacturer warranty and authorized dealer support.",
    status: "coming",
    icon: "ri-leaf-line",
    accentColor: "text-gray-500",
    badgeColor: "bg-gray-100 border-gray-200 text-gray-500",
    since: "Coming Soon",
    website: "https://www.ecoflow.com",
    shopFilter: "/shop",
    warrantyUnit: "2 years",
    warrantyBattery: "2 years (80% capacity retention)",
    warrantyNotes:
      "EcoFlow products carry a 2-year limited warranty. Extra Battery modules carry a separate 2-year warranty. Warranty service is handled through EcoFlow's US support portal. Once Iron Haven Supply is fully onboarded, we will manage all claims on your behalf.",
    products: [
      {
        name: "EcoFlow DELTA 2",
        category: "Portable Power Stations",
        warranty: "2 years",
        highlights: ["1024Wh capacity", "1800W AC output", "LFP battery — 3000+ cycles", "0–80% charge in 50 min"],
      },
      {
        name: "EcoFlow DELTA Pro",
        category: "Portable Power Stations",
        warranty: "2 years",
        highlights: ["3600Wh expandable to 25kWh", "3600W AC output", "Smart Home Panel compatible", "EV charging input"],
      },
      {
        name: "EcoFlow RIVER 2 Pro",
        category: "Portable Power Stations",
        warranty: "2 years",
        highlights: ["768Wh capacity", "800W AC output", "X-Stream fast charging", "Lightweight at 17.2 lbs"],
      },
      {
        name: "EcoFlow Smart Home Panel",
        category: "Home Backup",
        warranty: "2 years",
        highlights: ["Manages 10 home circuits", "Works with DELTA Pro", "Automatic transfer switch", "App-controlled"],
      },
    ],
    docStatus: [
      { label: "Authorized Reseller Agreement", verified: false },
      { label: "Reseller Certificate on File", verified: false },
      { label: "Warranty Pass-Through Confirmed", verified: false },
      { label: "Onboarding In Progress", verified: false },
    ],
  },
];

const schemaData = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Iron Haven Supply — Authorized Brand Partners",
  description:
    "Iron Haven Supply is an authorized reseller of Renogy, Nature's Generator, Rich Solar, and EcoFlow (coming soon). All purchases include full manufacturer warranty.",
  itemListElement: brands
    .filter((b) => b.status === "active")
    .map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Brand",
        name: b.name,
        url: b.website,
        description: b.description,
      },
    })),
};

export default function AuthorizedBrandsPage() {
  const [activeBrand, setActiveBrand] = useState<string>("renogy");
  const [openProduct, setOpenProduct] = useState<number | null>(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyName, setNotifyName] = useState("");
  const [notifyStatus, setNotifyStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const selected = brands.find((b) => b.id === activeBrand) ?? brands[0];

  const scrollToBrand = (id: string) => {
    setActiveBrand(id);
    setOpenProduct(null);
  };

  const handleNotifySubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!notifyEmail.trim()) return;
    setNotifyStatus("submitting");
    try {
      const body = new URLSearchParams();
      body.append("email", notifyEmail.trim());
      body.append("name", notifyName.trim());
      body.append("brand", "EcoFlow");
      body.append("source", "Authorized Brands Page — EcoFlow Notify Me");
      const res = await fetch("https://readdy.ai/api/form/d77qgkadqn9mib2tqc3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setNotifyStatus("success");
        setNotifyEmail("");
        setNotifyName("");
      } else {
        setNotifyStatus("error");
      }
    } catch {
      setNotifyStatus("error");
    }
  };

  return (
    <main className="font-sans bg-white min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-14 bg-gray-950 text-white text-center px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #f59e0b 0%, transparent 60%), radial-gradient(circle at 70% 50%, #f59e0b 0%, transparent 60%)" }}
        />
        <div className="relative max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-bold px-3 py-1 rounded-full mb-4">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-verified-badge-fill"></i>
            </div>
            Verified Authorized Dealer
          </span>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">
            Our Authorized Brand Partners
          </h1>
          <p className="text-gray-400 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            Every brand on this page has verified Iron Haven Supply as an authorized reseller. Your purchase includes full manufacturer warranty, genuine product, and our dedicated support.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "About Us", to: "/about" }, { label: "Authorized Brands" }]} />
        </div>
      </div>

      {/* Trust stats */}
      <section className="border-b border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: "ri-verified-badge-line", value: "3 Active", label: "Authorized Brands" },
            { icon: "ri-shield-check-line", value: "100%", label: "Warranty Valid on All Orders" },
            { icon: "ri-store-2-line", value: "Genuine", label: "No Gray-Market Products" },
            { icon: "ri-customer-service-2-line", value: "We File", label: "Warranty Claims For You" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                <i className={`${s.icon} text-amber-500 text-lg`}></i>
              </div>
              <div>
                <p className="font-extrabold text-gray-900 text-sm">{s.value}</p>
                <p className="text-gray-400 text-xs">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Main layout */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 flex flex-col lg:flex-row gap-8">

        {/* Sidebar brand selector */}
        <aside className="lg:w-56 shrink-0">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Brand Partners</p>
          <nav className="flex flex-row lg:flex-col gap-2 flex-wrap">
            {brands.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => scrollToBrand(b.id)}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-all cursor-pointer w-full ${
                  activeBrand === b.id
                    ? "bg-amber-500 text-white"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`${b.icon} text-base`}></i>
                </div>
                <span className="flex-1 whitespace-nowrap">{b.name}</span>
                {b.status === "coming" && (
                  <span className="text-[9px] font-bold bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">SOON</span>
                )}
                {b.status === "active" && activeBrand !== b.id && (
                  <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></div>
                )}
              </button>
            ))}
          </nav>

          {/* GMC verification callout */}
          <div className="mt-8 hidden lg:block bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg mb-3">
              <i className="ri-google-line text-lg text-amber-600"></i>
            </div>
            <p className="text-xs font-bold text-gray-900 mb-1">Google Merchant Center</p>
            <p className="text-xs text-gray-500 leading-relaxed mb-3">
              This page documents our authorized reseller status for GMC verification and brand compliance.
            </p>
            <Link
              to="/contact"
              className="block text-center bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Request Documentation
            </Link>
          </div>
        </aside>

        {/* Brand detail panel */}
        <div className="flex-1 min-w-0">

          {/* Brand header */}
          <div className={`rounded-2xl border-2 p-6 mb-6 ${selected.status === "coming" ? "border-dashed border-gray-200 bg-gray-50" : "border-gray-100 bg-white"}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl shrink-0 ${selected.status === "coming" ? "bg-gray-100" : "bg-amber-50"}`}>
                  <i className={`${selected.icon} text-3xl ${selected.status === "coming" ? "text-gray-400" : "text-amber-500"}`}></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h2 className="text-xl font-extrabold text-gray-900">{selected.name}</h2>
                    <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${selected.badgeColor}`}>
                      {selected.status === "active" ? "Authorized Reseller" : "Coming Soon"}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">{selected.tagline}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {selected.status === "active" ? `Partner since ${selected.since}` : "Onboarding in progress"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap shrink-0">
                <a
                  href={selected.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 border border-gray-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </div>
                  Brand Website
                </a>
                {selected.status === "active" && (
                  <Link
                    to={selected.shopFilter}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-white px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-store-2-line"></i>
                    </div>
                    Shop {selected.name}
                  </Link>
                )}
              </div>
            </div>

            <p className="text-gray-600 text-sm leading-relaxed mt-5 border-t border-gray-100 pt-5">
              {selected.description}
            </p>
          </div>

          {/* Reseller Documentation Status */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-shield-2-line text-base text-amber-500"></i>
              </div>
              <h3 className="font-extrabold text-gray-900 text-sm">Reseller Documentation Status</h3>
              <span className="text-xs text-gray-400 ml-auto">For GMC &amp; brand compliance</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {selected.docStatus.map((doc) => (
                <div
                  key={doc.label}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                    doc.verified
                      ? "bg-emerald-50 border-emerald-100"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full shrink-0 ${doc.verified ? "bg-emerald-100" : "bg-gray-200"}`}>
                    <i className={`text-sm ${doc.verified ? "ri-check-line text-emerald-600" : "ri-time-line text-gray-400"}`}></i>
                  </div>
                  <span className={`text-xs font-semibold ${doc.verified ? "text-emerald-800" : "text-gray-500"}`}>
                    {doc.label}
                  </span>
                </div>
              ))}
            </div>
            {selected.status === "coming" && (
              <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-information-line text-sm text-amber-500"></i>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  EcoFlow onboarding is currently in progress. Documentation will be updated once the authorized reseller agreement is finalized. Expected completion: Q2 2025.
                </p>
              </div>
            )}
          </div>

          {/* EcoFlow Notify Me form */}
          {selected.id === "ecoflow" && (
            <div className="bg-gray-900 rounded-2xl p-6 mb-6 relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 pointer-events-none"
                style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #f59e0b 0%, transparent 55%)" }}
              />
              <div className="relative">
                <div className="flex items-center gap-2.5 mb-1">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-lg shrink-0">
                    <i className="ri-notification-3-line text-amber-400 text-base"></i>
                  </div>
                  <h3 className="text-white font-extrabold text-base">Be First to Know When EcoFlow Launches</h3>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed mb-5 pl-10">
                  We&apos;re finalizing our authorized reseller agreement with EcoFlow. Drop your email and we&apos;ll notify you the moment DELTA &amp; RIVER series products go live — plus early access to any launch deals.
                </p>

                {notifyStatus === "success" ? (
                  <div className="flex items-center gap-3 bg-emerald-500/20 border border-emerald-500/30 rounded-xl px-5 py-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/30 rounded-full shrink-0">
                      <i className="ri-check-line text-emerald-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-emerald-300 font-bold text-sm">You&apos;re on the list!</p>
                      <p className="text-emerald-400/70 text-xs mt-0.5">We&apos;ll email you as soon as EcoFlow products are available through Iron Haven Supply.</p>
                    </div>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="ecoflow-notify-me"
                    onSubmit={handleNotifySubmit}
                    className="space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        name="name"
                        placeholder="Your name (optional)"
                        value={notifyName}
                        onChange={(e) => setNotifyName(e.target.value)}
                        className="flex-1 bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-amber-400/60 transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="Your email address"
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        className="flex-1 bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-amber-400/60 transition-colors"
                      />
                      <button
                        type="submit"
                        disabled={notifyStatus === "submitting"}
                        className="flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0"
                      >
                        {notifyStatus === "submitting" ? (
                          <>
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-loader-4-line animate-spin text-sm"></i>
                            </div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-bell-line text-sm"></i>
                            </div>
                            Notify Me
                          </>
                        )}
                      </button>
                    </div>
                    {notifyStatus === "error" && (
                      <p className="text-red-400 text-xs flex items-center gap-1.5">
                        <i className="ri-error-warning-line"></i>
                        Something went wrong. Please try again or <Link to="/contact" className="underline">contact us</Link>.
                      </p>
                    )}
                    <p className="text-gray-600 text-xs">
                      No spam, ever. Just a single email when EcoFlow goes live on Iron Haven Supply.
                    </p>
                  </form>
                )}

                <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-3 gap-3">
                  {[
                    { icon: "ri-flashlight-line", label: "DELTA 2", sub: "1024Wh · 1800W" },
                    { icon: "ri-battery-charge-line", label: "DELTA Pro", sub: "3600Wh · Expandable" },
                    { icon: "ri-drop-line", label: "RIVER 2 Pro", sub: "768Wh · Portable" },
                  ].map((item) => (
                    <div key={item.label} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-center">
                      <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                        <i className={`${item.icon} text-amber-400 text-base`}></i>
                      </div>
                      <p className="text-white text-xs font-bold">{item.label}</p>
                      <p className="text-gray-500 text-[10px] mt-0.5">{item.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Warranty Terms */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-star-line text-base text-amber-500"></i>
              </div>
              <h3 className="font-extrabold text-gray-900 text-sm">Warranty Terms</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg px-4 py-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Unit / Product Warranty</p>
                <p className="text-sm font-bold text-gray-900">{selected.warrantyUnit}</p>
              </div>
              <div className="bg-gray-50 rounded-lg px-4 py-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Battery Coverage</p>
                <p className="text-sm font-bold text-gray-900">{selected.warrantyBattery}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
              <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                <i className="ri-information-line text-sm text-amber-500"></i>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{selected.warrantyNotes}</p>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Link
                to="/warranty"
                className="text-xs font-semibold text-amber-600 hover:text-amber-500 flex items-center gap-1 cursor-pointer transition-colors"
              >
                View full warranty terms
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>

          {/* Product Catalog */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-box-3-line text-base text-amber-500"></i>
                </div>
                <h3 className="font-extrabold text-gray-900 text-sm">Product Catalog</h3>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {selected.products.length} products
                </span>
              </div>
              {selected.status === "active" && (
                <Link
                  to={selected.shopFilter}
                  className="text-xs font-semibold text-amber-600 hover:text-amber-500 flex items-center gap-1 cursor-pointer transition-colors whitespace-nowrap"
                >
                  Shop all
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              )}
            </div>

            <div className="space-y-2">
              {selected.products.map((product, idx) => (
                <div
                  key={product.name}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    openProduct === idx ? "border-amber-200 bg-amber-50/30" : "border-gray-100 bg-white hover:border-gray-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenProduct(openProduct === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg shrink-0">
                        <i className={`${selected.icon} text-sm text-gray-500`}></i>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{product.name}</p>
                        <p className="text-xs text-gray-400">{product.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <span className="hidden sm:block text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {product.warranty}
                      </span>
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                        openProduct === idx ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500"
                      }`}>
                        <i className={`text-sm ${openProduct === idx ? "ri-subtract-line" : "ri-add-line"}`}></i>
                      </div>
                    </div>
                  </button>
                  {openProduct === idx && (
                    <div className="px-4 pb-4">
                      <div className="flex items-center gap-1.5 mb-3">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-shield-check-line text-xs text-amber-500"></i>
                        </div>
                        <span className="text-xs text-amber-700 font-semibold">Warranty: {product.warranty}</span>
                      </div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {product.highlights.map((h) => (
                          <li key={h} className="flex items-center gap-2 text-xs text-gray-600">
                            <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                              <i className="ri-check-line text-emerald-500 text-xs"></i>
                            </div>
                            {h}
                          </li>
                        ))}
                      </ul>
                      {selected.status === "active" && (
                        <div className="mt-3 pt-3 border-t border-amber-100">
                          <Link
                            to={selected.shopFilter}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-500 cursor-pointer transition-colors"
                          >
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-store-2-line"></i>
                            </div>
                            Shop {selected.name} products
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact / claim CTA — only show for active brands */}
          {selected.status === "active" && (
            <div className="bg-gray-900 rounded-2xl px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-5">
              <div>
                <p className="text-white font-extrabold text-base">
                  Questions about {selected.name} products?
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Our team can help you choose the right system, verify warranty coverage, or start a claim.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-center shrink-0">
                <a
                  href="tel:8666054975"
                  className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-sm text-amber-400"></i>
                  </div>
                  (866) 605-4975
                </a>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-send-line text-sm"></i>
                  </div>
                  Contact Us
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom trust section */}
      <section className="bg-gray-50 border-t border-gray-100 py-12">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl font-extrabold text-gray-900">Why Authorized Reseller Status Matters</h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">
              Buying from an unauthorized seller voids your manufacturer warranty. Every purchase from Iron Haven Supply is protected.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                icon: "ri-shield-check-line",
                title: "Warranty Is Valid",
                body: "Manufacturer warranties are only honored when purchased through authorized channels. We are verified by each brand we carry.",
              },
              {
                icon: "ri-box-3-line",
                title: "Genuine Products",
                body: "No gray-market, refurbished, or counterfeit units. Every item ships new, in original packaging, directly from authorized distribution.",
              },
              {
                icon: "ri-customer-service-2-line",
                title: "We Advocate For You",
                body: "If you ever need a warranty claim, we file and manage it on your behalf — no navigating manufacturer portals alone.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-white border border-gray-100 rounded-xl p-5 flex gap-4 items-start">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                  <i className={`${item.icon} text-amber-500 text-lg`}></i>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm mb-1">{item.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center flex flex-wrap items-center justify-center gap-4 text-sm">
            <Link to="/warranty" className="text-amber-600 hover:underline font-medium cursor-pointer">Warranty Information</Link>
            <span className="text-gray-300">·</span>
            <Link to="/about" className="text-amber-600 hover:underline font-medium cursor-pointer">About Iron Haven Supply</Link>
            <span className="text-gray-300">·</span>
            <Link to="/contact" className="text-amber-600 hover:underline font-medium cursor-pointer">Contact Our Team</Link>
            <span className="text-gray-300">·</span>
            <Link to="/shop" className="text-amber-600 hover:underline font-medium cursor-pointer">Shop All Products</Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
